import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import './LegalDocument.css';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://letter-quest.com';

export default function PrivacyPolicyPage() {
  return (
    <main className="legal-page">
      <SeoHead
        title="Privacy Policy"
        description="How Letter Quest collects, uses, and shares information when you use our website and games."
        path="/privacy"
      />
      <h1>Privacy Policy</h1>
      <p className="legal-meta">
        Last updated: April 13, 2026 · Applies to:{' '}
        <a href={SITE_URL.replace(/\/$/, '')}>{SITE_URL.replace(/\/$/, '')}</a> and the Letter Quest mobile apps
        when they open this page.
      </p>

      <p>
        Letter Quest (&quot;we&quot;, &quot;us&quot;) provides word games on the web and on mobile. This policy
        describes what information we process, why, and your choices. If you use our mobile app, the same practices
        apply unless a store-specific notice says otherwise.
      </p>

      <h2>Information you provide</h2>
      <ul>
        <li>
          <strong>Account data.</strong> If you create an account (for example with email and password via Firebase
          Authentication), we store credentials and profile fields needed to run the service (such as display name
          where you set one).
        </li>
        <li>
          <strong>Gameplay and scores.</strong> When signed in, we may store game progress, scores, streaks, and
          leaderboard-related data in Firebase (Google Cloud Firestore) so features work across devices.
        </li>
        <li>
          <strong>Voluntary in-app ratings (mobile).</strong> If you choose to submit a low star rating inside the Letter
          Quest app while signed in, we may save your stars and optional short comment to Firestore (for example under
          &quot;appRatingFeedback&quot;) so we can read product feedback—we do not use that submission to post a review to
          the app stores automatically.
        </li>
      </ul>

      <h2>Information collected automatically</h2>
      <ul>
        <li>
          <strong>Technical data.</strong> Standard server and client logs may include IP address, device or browser
          type, and approximate timestamps when you load pages or call our backends.
        </li>
        <li>
          <strong>Analytics.</strong> On the website we may use privacy-oriented analytics (for example Vercel
          Analytics) to understand traffic and performance. You can limit some tracking via your browser or device
          settings.
        </li>
        <li>
          <strong>Advertising.</strong> The website may show ads (for example through Google AdSense). Ad partners may
          use cookies or similar technologies subject to their policies and, where required, your consent. The mobile
          app may use Google AdMob; see your device and Google settings for ad personalization choices.
        </li>
      </ul>

      <h2>How we use information</h2>
      <ul>
        <li>To provide, secure, and improve Letter Quest.</li>
        <li>To maintain accounts, leaderboards, streaks, and daily puzzle fairness.</li>
        <li>To comply with law and respond to lawful requests.</li>
      </ul>

      <h2>Sharing</h2>
      <p>
        We use service providers that process data on our behalf, including <strong>Google Firebase</strong> (auth and
        database), <strong>Google AdSense / AdMob</strong> (ads), and <strong>Vercel</strong> (website hosting and
        analytics). Their use is governed by their terms and privacy policies. We do not sell your personal information.
      </p>

      <h2>Retention</h2>
      <p>
        We keep account and gameplay data as long as your account exists or as needed for the purposes above. You may
        delete your account where the product supports it, or contact us using the support channel listed on the app
        store listing.
      </p>

      <h2>Children</h2>
      <p>
        Letter Quest is not directed at children under the age where parental consent is required in your region. If
        you believe we have collected a child&apos;s data in error, contact us so we can delete it.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on where you live, you may have rights to access, correct, delete, or export personal data, or to
        object to certain processing. Contact us through the support URL on your app store listing to make a request.
      </p>

      <h2>International transfers</h2>
      <p>
        Our providers may process data in the United States and other countries. By using Letter Quest, you understand
        that your information may be transferred as described here.
      </p>

      <h2>Changes</h2>
      <p>
        We may update this policy from time to time. We will post the new date at the top of this page. Material changes
        may be announced in the app or on the site where appropriate.
      </p>

      <p>
        Related: <Link to="/terms">Terms of Use</Link>
      </p>

      <p className="legal-disclaimer">
        This document is a practical summary for developers and users. It is not legal advice. Have qualified counsel
        review it before relying on it for regulated launches.
      </p>
    </main>
  );
}
